<div class="stac-onboarding">
  @if (flowScope() === 'full') {
    <div class="stac-onboarding__stepper" aria-label="STAC onboarding steps">
      @for (step of steps; track step.id; let i = $index) {
        @if (i > 0) {
          <div
            class="stac-onboarding__step-connector"
            [class.stac-onboarding__step-connector--completed]="stepState(steps[i - 1]!.id) === 'complete'"
          ></div>
        }
        <div
          class="stac-onboarding__step"
          [class.stac-onboarding__step--active]="stepState(step.id) === 'active'"
          [class.stac-onboarding__step--complete]="stepState(step.id) === 'complete'"
        >
          <div class="stac-onboarding__step-circle">
            @if (stepState(step.id) === 'complete') {
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M20 6L9 17L4 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            } @else {
              <span>{{ i + 1 }}</span>
            }
          </div>
          <span class="stac-onboarding__step-label">{{ step.label }}</span>
        </div>
      }
    </div>
  }

  <div class="stac-onboarding__content">
    @if (collectionCheckLoading()) {
      <div class="stac-onboarding__loading" role="status" aria-live="polite">
        @if (flowScope() === 'collection-edit' || flowScope() === 'item-edit' || checkExistingCollection()) {
          Loading STAC collection…
        } @else {
          Checking STAC collection…
        }
      </div>
    } @else {
    @if (displayedStep() === 'collection') {
      <form class="stac-onboarding__form" [formGroup]="collectionForm" (ngSubmit)="submitCollection()">
        @if (!isEditFlow()) {
          <div class="stac-onboarding__section-heading">
            <h3>Collection</h3>
          </div>
        }

        <div class="stac-onboarding__grid stac-onboarding__grid--single">
          <label class="stac-onboarding__field" [class.stac-onboarding__field--locked]="!!createdCollectionId()">
            <span>Collection Title <strong>*</strong></span>
            <input
              type="text"
              formControlName="title"
              placeholder="Enter collection title"
              [attr.readonly]="createdCollectionId() ? true : null"
            />
            @if (createdCollectionId()) {
              <small class="stac-onboarding__field-hint">Collection title cannot be changed after creation.</small>
            } @else if (collectionForm.controls.title.touched && collectionForm.controls.title.invalid) {
              <small>Collection Title is required.</small>
            }
          </label>
        </div>

        <div class="stac-onboarding__grid">
          <label class="stac-onboarding__field">
            <span>Start Date and Time <strong>*</strong></span>
            <div class="stac-datetime-field">
              <button
                type="button"
                class="stac-date-trigger"
                [class.stac-date-trigger--empty]="!calendarDisplayValue('startDate')"
                (click)="openCalendar('startDate', $event)"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                <span>{{ calendarDisplayValue('startDate') || 'Choose start date and time' }}</span>
              </button>
              <button
                type="button"
                class="stac-time-trigger"
                (click)="openTimePopover('startDate', $event)"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="2"/>
                  <path d="M12 7v5l3 2" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                <span>{{ timeInputValue('startDate') }}</span>
              </button>
            </div>
            @if (collectionForm.controls.startDate.touched && collectionForm.controls.startDate.invalid) {
              <small>Start date and time is required.</small>
            }
          </label>

          <label class="stac-onboarding__field">
            <span>End Date and Time <strong>*</strong></span>
            <div class="stac-datetime-field">
              <button
                type="button"
                class="stac-date-trigger"
                [class.stac-date-trigger--empty]="!calendarDisplayValue('endDate')"
                (click)="openCalendar('endDate', $event)"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                <span>{{ calendarDisplayValue('endDate') || 'Choose end date and time' }}</span>
              </button>
              <button
                type="button"
                class="stac-time-trigger"
                (click)="openTimePopover('endDate', $event)"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="2"/>
                  <path d="M12 7v5l3 2" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                <span>{{ timeInputValue('endDate') }}</span>
              </button>
            </div>
            @if (collectionForm.controls.endDate.touched && collectionForm.controls.endDate.invalid) {
              <small>End date and time is required.</small>
            }
            @if (collectionForm.touched && collectionForm.hasError('dateRange')) {
              <small>End date and time must be on or after the start date and time.</small>
            }
          </label>
        </div>

        <label class="stac-onboarding__field">
          <span>Description <strong>*</strong></span>
          <div class="stac-onboarding__textarea-shell">
            <textarea
              formControlName="description"
              rows="3"
              class="stac-onboarding__textarea stac-onboarding__textarea--with-count"
              [attr.maxlength]="descriptionMaxLength"
              placeholder="Brief description of the dataset (max 2000 characters)."
            ></textarea>
            <span
              class="stac-onboarding__textarea-count"
              [class.stac-onboarding__textarea-count--warn]="descriptionLength() > descriptionMaxLength * 0.9"
              [class.stac-onboarding__textarea-count--limit]="descriptionLength() >= descriptionMaxLength"
              aria-hidden="true"
            >{{ descriptionLength() }}/{{ descriptionMaxLength }}</span>
          </div>
          @if (collectionForm.controls.description.touched && collectionForm.controls.description.invalid) {
            <small>Description is required.</small>
          }
        </label>

        @if (!bbox()) {
          <section class="stac-onboarding__bbox">
            <div class="stac-onboarding__bbox-header">
              <div>
                <h4>Spatial Extent <strong>*</strong></h4>
                <p>CRS84 bbox: west, south, east, north</p>
              </div>
              <button type="button" class="stac-onboarding__map-button" (click)="openBboxMap()">
                Draw BBOX
              </button>
            </div>
          </section>
        } @else {
          <section class="stac-onboarding__bbox-plotted" aria-label="Spatial extent">
            <div class="stac-onboarding__bbox-plotted-side">
              <div class="stac-onboarding__bbox-plotted-heading">
                <h4>Spatial Extent <strong>*</strong></h4>
                <p>CRS84 bbox: west, south, east, north</p>
              </div>
              <div class="stac-onboarding__bbox-actions">
                <button type="button" class="stac-onboarding__bbox-action stac-onboarding__bbox-action--edit" (click)="openBboxMap()">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path d="M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  Edit
                </button>
                <button type="button" class="stac-onboarding__bbox-action stac-onboarding__bbox-action--delete" (click)="clearCollectionBbox()">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                    <polyline points="3 6 5 6 21 6"></polyline>
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                  </svg>
                  Delete
                </button>
              </div>
              <dl class="stac-onboarding__bbox-coords">
                <div class="stac-onboarding__bbox-coord">
                  <dt>West</dt>
                  <dd>{{ bboxValue(0) }}</dd>
                </div>
                <div class="stac-onboarding__bbox-coord">
                  <dt>South</dt>
                  <dd>{{ bboxValue(1) }}</dd>
                </div>
                <div class="stac-onboarding__bbox-coord">
                  <dt>East</dt>
                  <dd>{{ bboxValue(2) }}</dd>
                </div>
                <div class="stac-onboarding__bbox-coord">
                  <dt>North</dt>
                  <dd>{{ bboxValue(3) }}</dd>
                </div>
              </dl>
            </div>
            <div class="stac-onboarding__bbox-preview-wrap">
              <div #bboxPreviewMap class="stac-onboarding__bbox-preview" aria-label="Bbox map preview"></div>
              <div class="stac-map-zoom" aria-label="Map zoom controls">
                <button type="button" class="stac-map-zoom__btn" title="Zoom in" (click)="zoomPreviewMap(1)">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" aria-hidden="true">
                    <line x1="12" y1="5" x2="12" y2="19" />
                    <line x1="5" y1="12" x2="19" y2="12" />
                  </svg>
                </button>
                <button type="button" class="stac-map-zoom__btn" title="Zoom out" (click)="zoomPreviewMap(-1)">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" aria-hidden="true">
                    <line x1="5" y1="12" x2="19" y2="12" />
                  </svg>
                </button>
              </div>
            </div>
          </section>
        }
      </form>
    } @else if (displayedStep() === 'item') {
      <section class="stac-onboarding__form" [formGroup]="itemForm">
        @if (!isEditFlow()) {
          <div class="stac-onboarding__section-heading">
            <h3>STAC Item</h3>
            <span class="stac-onboarding__dataset-id">{{ collectionId() }}</span>
          </div>
        }

        @if (showExistingItemTab() && flowScope() !== 'item-edit') {
          <div class="stac-onboarding__tabs" role="tablist" aria-label="STAC item mode">
            <button
              type="button"
              role="tab"
              class="stac-onboarding__tab"
              [class.stac-onboarding__tab--active]="itemMode() === 'new'"
              [attr.aria-selected]="itemMode() === 'new'"
              (click)="setItemMode('new')"
            >
              New Item
            </button>
            <button
              type="button"
              role="tab"
              class="stac-onboarding__tab"
              [class.stac-onboarding__tab--active]="itemMode() === 'existing'"
              [attr.aria-selected]="itemMode() === 'existing'"
              (click)="setItemMode('existing')"
            >
              Existing Item
            </button>
          </div>
        }

        @if (isEditFlow() && itemMode() === 'existing') {
          <label class="stac-onboarding__field">
            <span>STAC Item <strong>*</strong></span>
            <div class="stac-custom-dropdown">
              <button
                type="button"
                class="stac-custom-dropdown__trigger"
                [class.stac-custom-dropdown__trigger--open]="existingItemDropdownOpen()"
                [attr.aria-expanded]="existingItemDropdownOpen()"
                [disabled]="existingItemsLoading() || existingItems().length === 0"
                (click)="toggleExistingItemDropdown($event)"
              >
                @if (!selectedExistingItemId()) {
                  <span class="stac-custom-dropdown__placeholder">Select STAC item</span>
                } @else {
                  <span class="stac-custom-dropdown__value">{{ selectedExistingItemId() }}</span>
                }
                <svg
                  class="stac-custom-dropdown__chevron"
                  [class.stac-custom-dropdown__chevron--open]="existingItemDropdownOpen()"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  aria-hidden="true"
                >
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </button>
              @if (existingItemDropdownOpen()) {
                <ul class="stac-custom-dropdown__panel" role="listbox" (click)="$event.stopPropagation()">
                  @for (item of existingItems(); track item.id) {
                    <li class="stac-custom-dropdown__item" role="presentation">
                      <button
                        type="button"
                        class="stac-custom-dropdown__option"
                        role="option"
                        [class.stac-custom-dropdown__option--active]="selectedExistingItemId() === item.id"
                        [attr.aria-selected]="selectedExistingItemId() === item.id"
                        (click)="selectExistingItemFromDropdown(item.id)"
                      >
                        {{ item.id }}
                      </button>
                    </li>
                  }
                </ul>
              }
            </div>
            @if (existingItemsLoading()) {
              <small>Loading existing items...</small>
            } @else if (existingItems().length === 0) {
              <small>No existing items were found for this collection.</small>
            }
          </label>

          @if (existingItemDetailsLoading()) {
            <div class="stac-onboarding__loading stac-onboarding__loading--inline" role="status" aria-live="polite">
              Loading item metadata…
            </div>
          }
        }

        @if (flowScope() === 'item-edit') {
          <div class="stac-onboarding__tabs" role="tablist" aria-label="STAC item mode">
            <button
              type="button"
              role="tab"
              class="stac-onboarding__tab"
              [class.stac-onboarding__tab--active]="itemMode() === 'existing'"
              [attr.aria-selected]="itemMode() === 'existing'"
              (click)="setItemEditMode('existing')"
            >
              Existing Item
            </button>
            <button
              type="button"
              role="tab"
              class="stac-onboarding__tab"
              [class.stac-onboarding__tab--active]="itemMode() === 'new'"
              [attr.aria-selected]="itemMode() === 'new'"
              (click)="setItemEditMode('new')"
            >
              New Item
            </button>
          </div>

          @if (itemMode() === 'existing') {
          <label class="stac-onboarding__field">
            <span>STAC Item <strong>*</strong></span>
            <div class="stac-custom-dropdown">
              <button
                type="button"
                class="stac-custom-dropdown__trigger"
                [class.stac-custom-dropdown__trigger--open]="existingItemDropdownOpen()"
                [attr.aria-expanded]="existingItemDropdownOpen()"
                [disabled]="existingItemsLoading() || existingItems().length === 0"
                (click)="toggleExistingItemDropdown($event)"
              >
                @if (!selectedExistingItemId()) {
                  <span class="stac-custom-dropdown__placeholder">Select STAC item</span>
                } @else {
                  <span class="stac-custom-dropdown__value">{{ selectedExistingItemId() }}</span>
                }
                <svg
                  class="stac-custom-dropdown__chevron"
                  [class.stac-custom-dropdown__chevron--open]="existingItemDropdownOpen()"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  aria-hidden="true"
                >
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </button>
              @if (existingItemDropdownOpen()) {
                <ul class="stac-custom-dropdown__panel" role="listbox" (click)="$event.stopPropagation()">
                  @for (item of existingItems(); track item.id) {
                    <li class="stac-custom-dropdown__item" role="presentation">
                      <button
                        type="button"
                        class="stac-custom-dropdown__option"
                        role="option"
                        [class.stac-custom-dropdown__option--active]="selectedExistingItemId() === item.id"
                        [attr.aria-selected]="selectedExistingItemId() === item.id"
                        (click)="selectExistingItemFromDropdown(item.id)"
                      >
                        {{ item.id }}
                      </button>
                    </li>
                  }
                </ul>
              }
            </div>
            @if (existingItemsLoading()) {
              <small>Loading existing items...</small>
            } @else if (existingItems().length === 0) {
              <small>No existing items were found for this collection.</small>
            }
          </label>

          @if (existingItemDetailsLoading()) {
            <div class="stac-onboarding__loading stac-onboarding__loading--inline" role="status" aria-live="polite">
              Loading item metadata…
            </div>
          }
          }
        }

        @if (itemMode() === 'new' || !showExistingItemTab() || (isEditFlow() && itemMode() === 'existing' && selectedExistingItemId() && !existingItemDetailsLoading()) || (flowScope() === 'item-edit' && (itemMode() === 'new' || (selectedExistingItemId() && !existingItemDetailsLoading())))) {
          <div class="stac-onboarding__grid">
            <label class="stac-onboarding__field" [class.stac-onboarding__field--locked]="!!createdItemId()">
              <span>Name <strong>*</strong></span>
              <input
                type="text"
                formControlName="id"
                maxlength="50"
                placeholder="item_name"
                [attr.readonly]="createdItemId() ? true : null"
                (input)="createdItemId() ? null : sanitizeItemId($event)"
              />
              @if (createdItemId()) {
                <small class="stac-onboarding__field-hint">Item name cannot be changed after creation.</small>
              } @else if (itemForm.controls.id.touched && itemForm.controls.id.invalid) {
                <small>Use up to 50 letters, numbers, underscores, or hyphens.</small>
              }
            </label>

            <label class="stac-onboarding__field">
              <span>Date and Time <strong>*</strong></span>
              <div class="stac-datetime-field">
                <button
                  type="button"
                  class="stac-date-trigger"
                  [class.stac-date-trigger--empty]="!calendarDisplayValue('itemDate')"
                  (click)="openCalendar('itemDate', $event)"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  <span>{{ calendarDisplayValue('itemDate') || 'Choose date and time' }}</span>
                </button>
                <button
                  type="button"
                  class="stac-time-trigger"
                  (click)="openTimePopover('itemDate', $event)"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="2"/>
                    <path d="M12 7v5l3 2" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  <span>{{ timeInputValue('itemDate') }}</span>
                </button>
              </div>
              @if (itemForm.controls.datetime.touched && itemForm.controls.datetime.invalid) {
                <small>Date and time is required.</small>
              }
              @if (itemForm.controls.datetime.touched && itemDateOutsideCollectionRange()) {
                <small>Item date and time must be within the collection date and time range ({{ collectionDateRangeDisplay() }}).</small>
              }
            </label>
          </div>

          @if (!itemGeometry()) {
            <section class="stac-onboarding__bbox">
              <div class="stac-onboarding__bbox-header">
                <div>
                  <h4>Geometry <strong>*</strong></h4>
                  <p>Upload or paste one GeoJSON geometry for this STAC item.</p>
                </div>
                <button type="button" class="stac-onboarding__map-button" (click)="openItemGeometryMap()">
                  Add Geometry
                </button>
              </div>
            </section>
          } @else {
            <section class="stac-onboarding__bbox-plotted" aria-label="Item geometry">
              <div class="stac-onboarding__bbox-plotted-side">
                <div class="stac-onboarding__bbox-plotted-heading">
                  <h4>Geometry <strong>*</strong></h4>
                  <p>{{ itemGeometryLabel() }}</p>
                </div>
                <div class="stac-onboarding__bbox-actions">
                  <button type="button" class="stac-onboarding__bbox-action stac-onboarding__bbox-action--edit" (click)="openItemGeometryMap()">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <path d="M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                    Edit
                  </button>
                  <button type="button" class="stac-onboarding__bbox-action stac-onboarding__bbox-action--delete" (click)="clearItemGeometry()">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                      <polyline points="3 6 5 6 21 6"></polyline>
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                    </svg>
                    Delete
                  </button>
                </div>
                <dl class="stac-onboarding__bbox-coords">
                  <div class="stac-onboarding__bbox-coord">
                    <dt>West</dt>
                    <dd>{{ itemBboxValue(0) }}</dd>
                  </div>
                  <div class="stac-onboarding__bbox-coord">
                    <dt>South</dt>
                    <dd>{{ itemBboxValue(1) }}</dd>
                  </div>
                  <div class="stac-onboarding__bbox-coord">
                    <dt>East</dt>
                    <dd>{{ itemBboxValue(2) }}</dd>
                  </div>
                  <div class="stac-onboarding__bbox-coord">
                    <dt>North</dt>
                    <dd>{{ itemBboxValue(3) }}</dd>
                  </div>
                </dl>
              </div>
              <div class="stac-onboarding__bbox-preview-wrap">
                <div #bboxPreviewMap class="stac-onboarding__bbox-preview" aria-label="Item geometry map preview"></div>
                <div class="stac-map-zoom" aria-label="Map zoom controls">
                  <button type="button" class="stac-map-zoom__btn" title="Zoom in" (click)="zoomPreviewMap(1)">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" aria-hidden="true">
                      <line x1="12" y1="5" x2="12" y2="19" />
                      <line x1="5" y1="12" x2="19" y2="12" />
                    </svg>
                  </button>
                  <button type="button" class="stac-map-zoom__btn" title="Zoom out" (click)="zoomPreviewMap(-1)">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" aria-hidden="true">
                      <line x1="5" y1="12" x2="19" y2="12" />
                    </svg>
                  </button>
                </div>
              </div>
            </section>
          }

          <section class="stac-onboarding__properties">
            <div class="stac-onboarding__properties-header">
              <div>
                <h4>Properties</h4>
                <p>Add optional metadata such as driver, projection, and raster dimensions.</p>
              </div>
              <button
                type="button"
                class="stac-onboarding__map-button"
                (click)="openPropertiesModal()"
              >
                Add Properties
              </button>
            </div>

            @if (itemPropertiesCount() > 0) {
              <div class="stac-onboarding__properties-summary">
                <div class="stac-onboarding__properties-summary-header">
                  <strong>{{ itemPropertiesCount() }} propert{{ itemPropertiesCount() === 1 ? 'y' : 'ies' }} added</strong>
                  <button type="button" (click)="openPropertiesModal()">Edit</button>
                </div>
                <dl class="stac-onboarding__properties-list">
                  @for (row of itemProperties(); track row.id) {
                    <div class="stac-onboarding__properties-item">
                      <dt>{{ row.key }}</dt>
                      <dd>{{ formatPropertyPreviewValue(row.value) }}</dd>
                    </div>
                  }
                </dl>
              </div>
            }
          </section>
        } @else if (flowScope() !== 'item-edit' && !isEditFlow()) {
          <label class="stac-onboarding__field">
            <span>STAC Item <strong>*</strong></span>
            <div class="stac-custom-dropdown">
              <button
                type="button"
                class="stac-custom-dropdown__trigger"
                [class.stac-custom-dropdown__trigger--open]="existingItemDropdownOpen()"
                [attr.aria-expanded]="existingItemDropdownOpen()"
                [disabled]="existingItemsLoading() || existingItems().length === 0"
                (click)="toggleExistingItemDropdown($event)"
              >
                @if (!selectedExistingItemId()) {
                  <span class="stac-custom-dropdown__placeholder">Select STAC item</span>
                } @else {
                  <span class="stac-custom-dropdown__value">{{ selectedExistingItemId() }}</span>
                }
                <svg
                  class="stac-custom-dropdown__chevron"
                  [class.stac-custom-dropdown__chevron--open]="existingItemDropdownOpen()"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  aria-hidden="true"
                >
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </button>
              @if (existingItemDropdownOpen()) {
                <ul class="stac-custom-dropdown__panel" role="listbox" (click)="$event.stopPropagation()">
                  @for (item of existingItems(); track item.id) {
                    <li class="stac-custom-dropdown__item" role="presentation">
                      <button
                        type="button"
                        class="stac-custom-dropdown__option"
                        role="option"
                        [class.stac-custom-dropdown__option--active]="selectedExistingItemId() === item.id"
                        [attr.aria-selected]="selectedExistingItemId() === item.id"
                        (click)="selectExistingItemFromDropdown(item.id)"
                      >
                        {{ item.id }}
                      </button>
                    </li>
                  }
                </ul>
              }
            </div>
            @if (existingItemsLoading()) {
              <small>Loading existing items...</small>
            } @else if (existingItems().length === 0) {
              <small>No existing items were found for this collection.</small>
            }
          </label>

          @if (existingItemDetailsLoading()) {
            <div class="stac-onboarding__loading stac-onboarding__loading--inline" role="status" aria-live="polite">
              Loading item metadata…
            </div>
          }

          @if (selectedExistingItemId() && !existingItemDetailsLoading()) {
            <div class="stac-onboarding__grid">
              <div class="stac-onboarding__field">
                <span>Name</span>
                <div class="stac-onboarding__readonly">{{ itemForm.controls.id.value }}</div>
              </div>
              <div class="stac-onboarding__field">
                <span>Date</span>
                <div class="stac-onboarding__readonly">{{ calendarDisplayValue('itemDate') || '—' }}</div>
              </div>
            </div>

            @if (itemGeometry()) {
              <section class="stac-onboarding__bbox-plotted" aria-label="Item geometry">
                <div class="stac-onboarding__bbox-plotted-side">
                  <div class="stac-onboarding__bbox-plotted-heading">
                    <h4>Geometry</h4>
                    <p>{{ itemGeometryLabel() }}</p>
                  </div>
                  <dl class="stac-onboarding__bbox-coords">
                    <div class="stac-onboarding__bbox-coord">
                      <dt>West</dt>
                      <dd>{{ itemBboxValue(0) }}</dd>
                    </div>
                    <div class="stac-onboarding__bbox-coord">
                      <dt>South</dt>
                      <dd>{{ itemBboxValue(1) }}</dd>
                    </div>
                    <div class="stac-onboarding__bbox-coord">
                      <dt>East</dt>
                      <dd>{{ itemBboxValue(2) }}</dd>
                    </div>
                    <div class="stac-onboarding__bbox-coord">
                      <dt>North</dt>
                      <dd>{{ itemBboxValue(3) }}</dd>
                    </div>
                  </dl>
                </div>
                <div class="stac-onboarding__bbox-preview-wrap">
                  <div #bboxPreviewMap class="stac-onboarding__bbox-preview" aria-label="Item geometry map preview"></div>
                  <div class="stac-map-zoom" aria-label="Map zoom controls">
                    <button type="button" class="stac-map-zoom__btn" title="Zoom in" (click)="zoomPreviewMap(1)">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" aria-hidden="true">
                        <line x1="12" y1="5" x2="12" y2="19" />
                        <line x1="5" y1="12" x2="19" y2="12" />
                      </svg>
                    </button>
                    <button type="button" class="stac-map-zoom__btn" title="Zoom out" (click)="zoomPreviewMap(-1)">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" aria-hidden="true">
                        <line x1="5" y1="12" x2="19" y2="12" />
                      </svg>
                    </button>
                  </div>
                </div>
              </section>
            }

            @if (itemPropertiesCount() > 0) {
              <section class="stac-onboarding__properties">
                <div class="stac-onboarding__properties-header">
                  <div>
                    <h4>Properties</h4>
                    <p>Metadata attached to this STAC item.</p>
                  </div>
                </div>
                <div class="stac-onboarding__properties-summary">
                  <div class="stac-onboarding__properties-summary-header">
                    <strong>{{ itemPropertiesCount() }} propert{{ itemPropertiesCount() === 1 ? 'y' : 'ies' }}</strong>
                  </div>
                  <dl class="stac-onboarding__properties-list">
                    @for (row of itemProperties(); track row.id) {
                      <div class="stac-onboarding__properties-item">
                        <dt>{{ row.key }}</dt>
                        <dd>{{ formatPropertyPreviewValue(row.value) }}</dd>
                      </div>
                    }
                  </dl>
                </div>
              </section>
            }
          }
        }
      </section>
    } @else {
      <section class="stac-onboarding__form" [formGroup]="assetsForm">
        @if (!isEditFlow()) {
          <div class="stac-onboarding__section-heading">
            <h3>Assets</h3>
            <span class="stac-onboarding__dataset-id">{{ assetsItemLabel() }}</span>
          </div>
        }

        @if (existingItemAssetsLoading()) {
          <div class="stac-onboarding__loading stac-onboarding__loading--inline" role="status" aria-live="polite">
            Loading existing assets…
          </div>
        }

        @if (showAssetsTabs()) {
          <div class="stac-onboarding__tabs" role="tablist" aria-label="Asset management">
            <button
              type="button"
              role="tab"
              class="stac-onboarding__tab"
              [class.stac-onboarding__tab--active]="assetsTab() === 'view'"
              [attr.aria-selected]="assetsTab() === 'view'"
              (click)="setAssetsTab('view')"
            >
              View Files
            </button>
            <button
              type="button"
              role="tab"
              class="stac-onboarding__tab"
              [class.stac-onboarding__tab--active]="assetsTab() === 'upload'"
              [attr.aria-selected]="assetsTab() === 'upload'"
              (click)="setAssetsTab('upload')"
            >
              Upload New Assets
            </button>
          </div>
        }

        @if (showAssetsTabs() && assetsTab() === 'view') {
          <div class="stac-existing-assets">
            <div class="stac-existing-assets__table-wrap">
              <table class="stac-existing-assets__table">
                <thead>
                  <tr>
                    <th scope="col">Title</th>
                    <th scope="col">Description</th>
                    <th scope="col">Role</th>
                    <th scope="col">Type</th>
                    <th scope="col">Size</th>
                    <th scope="col" class="stac-existing-assets__action-header">Action</th>
                  </tr>
                </thead>
                <tbody>
                  @for (asset of existingItemAssets(); track asset.id) {
                    <tr [class.stac-existing-assets__row--dropdown-open]="isAssetActionDropdownOpen(asset.id)">
                      <td class="stac-existing-assets__title">{{ asset.title }}</td>
                      <td>{{ asset.description || '—' }}</td>
                      <td>
                        @if (asset.roles.length) {
                          <div class="stac-existing-assets__roles">
                            @for (role of asset.roles; track role) {
                              <span class="stac-existing-assets__role-badge">{{ role }}</span>
                            }
                          </div>
                        } @else {
                          —
                        }
                      </td>
                      <td>{{ asset.type || '—' }}</td>
                      <td>{{ formatExistingAssetSize(asset.size) }}</td>
                      <td class="stac-existing-assets__action">
                        <div
                          class="stac-existing-assets__action-cell"
                          [class.stac-existing-assets__action-cell--open]="isAssetActionDropdownOpen(asset.id)"
                        >
                          <button
                            type="button"
                            class="stac-existing-assets__action-btn"
                            [class.stac-existing-assets__action-btn--active]="isAssetActionDropdownOpen(asset.id)"
                            [attr.aria-expanded]="isAssetActionDropdownOpen(asset.id)"
                            aria-haspopup="menu"
                            (click)="toggleAssetActionDropdown($event, asset)"
                          >
                            <span class="stac-existing-assets__action-label">Action</span>
                            <svg
                              class="stac-existing-assets__action-chevron"
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              stroke-width="2"
                              aria-hidden="true"
                            >
                              <polyline points="6 9 12 15 18 9"></polyline>
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
          </div>
        }

        @if (!showAssetsTabs() || assetsTab() === 'upload') {
        <div class="stac-assets-storage-section">
          <label class="stac-onboarding__field">
            <span>Storage <strong>*</strong></span>
            <div class="stac-custom-dropdown">
              <button
                type="button"
                class="stac-custom-dropdown__trigger"
                [class.stac-custom-dropdown__trigger--open]="storageDropdownOpen()"
                [attr.aria-expanded]="storageDropdownOpen()"
                [disabled]="bucketsLoading() || buckets().length === 0"
                (click)="toggleStorageDropdown($event)"
              >
                @if (!selectedBucketId()) {
                  <span class="stac-custom-dropdown__placeholder">Select Storage bucket</span>
                } @else {
                  <span class="stac-custom-dropdown__value">{{ getSelectedBucketLabel() }}</span>
                }
                <svg
                  class="stac-custom-dropdown__chevron"
                  [class.stac-custom-dropdown__chevron--open]="storageDropdownOpen()"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  aria-hidden="true"
                >
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </button>
              @if (storageDropdownOpen()) {
                <ul class="stac-custom-dropdown__panel" role="listbox" (click)="$event.stopPropagation()">
                  @for (bucket of buckets(); track bucketIdentifier(bucket)) {
                    <li class="stac-custom-dropdown__item" role="presentation">
                      <button
                        type="button"
                        class="stac-custom-dropdown__option"
                        role="option"
                        [class.stac-custom-dropdown__option--active]="selectedBucketId() === bucketIdentifier(bucket)"
                        [attr.aria-selected]="selectedBucketId() === bucketIdentifier(bucket)"
                        (click)="selectStorageBucket(bucketIdentifier(bucket))"
                      >
                        {{ bucketIdentifier(bucket) }} ({{ bucket.readAccess }})
                      </button>
                    </li>
                  }
                </ul>
              }
            </div>
            @if (bucketsLoading()) {
              <small>Loading storage...</small>
            } @else if (buckets().length === 0) {
              <small>No private storage buckets were found.</small>
            }
          </label>
        </div>

        <div class="stac-assets-list-heading">
          <span>Asset Files</span>
          <span class="stac-assets-list-heading__count">{{ assetRows.length }} asset{{ assetRows.length !== 1 ? 's' : '' }}</span>
        </div>

        <div class="stac-assets-table" formArrayName="rows">
          @for (row of assetRows.controls; track row; let i = $index) {
            <div
              class="stac-assets-row"
              [class.stac-assets-row--accordion]="useAssetAccordion()"
              [class.stac-assets-row--expanded]="isAssetExpanded(i)"
            >
              @if (useAssetAccordion()) {
                <button
                  type="button"
                  class="stac-assets-accordion__header"
                  [attr.aria-expanded]="isAssetExpanded(i)"
                  (click)="toggleAssetAccordion(i)"
                >
                  <span class="stac-assets-accordion__label">
                    <span class="stac-assets-accordion__badge">{{ i + 1 }}</span>
                    <span class="stac-assets-accordion__title">{{ getAssetAccordionLabel(i) }}</span>
                  </span>
                  <svg
                    class="stac-assets-accordion__chevron"
                    [class.stac-assets-accordion__chevron--open]="isAssetExpanded(i)"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    aria-hidden="true"
                  >
                    <path d="M6 9L12 15L18 9" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </button>
              }

              @if (!useAssetAccordion() || isAssetExpanded(i)) {
                <div class="stac-assets-accordion__body" [formGroupName]="i">
                  <div class="stac-onboarding__grid">
                    <label class="stac-onboarding__field">
                      <span>Title <strong>*</strong></span>
                      <input type="text" formControlName="title" placeholder="Asset title" />
                    </label>
                    <label class="stac-onboarding__field">
                      <span>Role <strong>*</strong></span>
                      <div class="stac-custom-dropdown">
                        <button
                          type="button"
                          class="stac-custom-dropdown__trigger"
                          [class.stac-custom-dropdown__trigger--open]="isAssetRoleDropdownOpen(i)"
                          [attr.aria-expanded]="isAssetRoleDropdownOpen(i)"
                          (click)="toggleAssetRoleDropdown(i, $event)"
                        >
                          <span class="stac-custom-dropdown__value">{{ row.get('role')?.value }}</span>
                          <svg
                            class="stac-custom-dropdown__chevron"
                            [class.stac-custom-dropdown__chevron--open]="isAssetRoleDropdownOpen(i)"
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            aria-hidden="true"
                          >
                            <polyline points="6 9 12 15 18 9"></polyline>
                          </svg>
                        </button>
                        @if (isAssetRoleDropdownOpen(i)) {
                          <ul class="stac-custom-dropdown__panel" role="listbox" (click)="$event.stopPropagation()">
                            @for (role of assetRoles; track role) {
                              <li class="stac-custom-dropdown__item" role="presentation">
                                <button
                                  type="button"
                                  class="stac-custom-dropdown__option"
                                  role="option"
                                  [class.stac-custom-dropdown__option--active]="row.get('role')?.value === role"
                                  [attr.aria-selected]="row.get('role')?.value === role"
                                  (click)="selectAssetRole(i, role)"
                                >
                                  {{ role }}
                                </button>
                              </li>
                            }
                          </ul>
                        }
                      </div>
                    </label>
                  </div>

                  <label class="stac-onboarding__field">
                    <span>Description <strong>*</strong></span>
                    <textarea formControlName="description" rows="3" placeholder="Asset description"></textarea>
                  </label>

                  <div class="stac-assets-row__file-section">
                    <span class="stac-assets-row__file-label">File <strong>*</strong></span>
                    @if (!row.get('fileObject')?.value) {
                      <div
                        class="stac-assets-dropzone"
                        [class.stac-assets-dropzone--active]="assetFileDragActive() === i"
                        role="button"
                        tabindex="0"
                        (click)="triggerAssetFileInput(i, $event)"
                        (keydown.enter)="triggerAssetFileInput(i)"
                        (keydown.space)="$event.preventDefault(); triggerAssetFileInput(i)"
                        (dragover)="onAssetDragOver($event, i)"
                        (dragleave)="onAssetDragLeave($event, i)"
                        (drop)="onAssetDrop($event, i)"
                      >
                        <svg class="stac-assets-dropzone__icon" width="32" height="32" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                          <path d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                          <path d="M7 10L12 5L17 10" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                          <path d="M12 5V17" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                        <p class="stac-assets-dropzone__text">
                          Drag and drop your file here or
                          <button type="button" class="stac-assets-dropzone__browse" (click)="triggerAssetFileInput(i, $event); $event.stopPropagation()">
                            browse
                          </button>
                        </p>
                        <input
                          #assetFileInput
                          type="file"
                          class="stac-assets-dropzone__input"
                          (change)="onAssetFileSelected($event, i)"
                        />
                      </div>
                    } @else {
                      <div class="stac-assets-file">
                        <div class="stac-assets-file__info">
                          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                            <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                            <path d="M14 2V8H20" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                          </svg>
                          <div>
                            <p class="stac-assets-file__name">{{ row.get('fileName')?.value }}</p>
                            <p class="stac-assets-file__size">{{ formatAssetFileSize(getAssetFileSize(i)) }}</p>
                          </div>
                        </div>
                        <button type="button" class="stac-assets-file__remove" (click)="clearAssetFile(i)">
                          Remove
                        </button>
                      </div>
                    }
                  </div>

                  @if (useAssetAccordion()) {
                    <div class="stac-assets-row__actions">
                      <button
                        type="button"
                        class="stac-assets-row__remove"
                        [disabled]="assetRows.length === 1"
                        (click)="removeAssetRow(i)"
                        aria-label="Remove asset"
                      >
                        Remove asset
                      </button>
                    </div>
                  }

                  @if (uploadStates()[i]; as upload) {
                    <div class="stac-assets-row__progress" role="status">
                      <span>{{ upload.fileName }} — {{ upload.status }}</span>
                      <strong>{{ upload.progress }}%</strong>
                      <div><span [style.width.%]="upload.progress"></span></div>
                    </div>
                  }
                </div>
              }
            </div>
          }
        </div>

        <button type="button" class="stac-onboarding__map-button stac-onboarding__map-button--inline stac-onboarding__map-button--add-asset" (click)="addAssetRow()">
          Add Asset
        </button>
        }
      </section>
    }
    }
  </div>

  @if (flowScope() !== 'embedded' && !collectionCheckLoading()) {
    <footer class="stac-onboarding__footer">
      @if (showModalPreviousButton()) {
        <button type="button" class="stac-onboarding__btn stac-onboarding__btn--secondary" (click)="onModalPrevious()">
          ← Previous
        </button>
      }
      <div class="stac-onboarding__footer-actions">
        @if (displayedStep() === 'collection') {
          <button
            type="button"
            class="stac-onboarding__btn stac-onboarding__btn--primary"
            [disabled]="submitting() || !canSubmitCollection() || (flowScope() === 'collection-edit' && !createdCollectionId())"
            (click)="submitCollection()"
          >
            @if (flowScope() === 'collection-edit' || isEditFlow()) {
              {{
                submitting()
                  ? 'Updating...'
                  : hasCollectionChanges()
                    ? 'Update Collection'
                    : 'Next'
              }}
            } @else {
              {{
                submitting()
                  ? (createdCollectionId() ? 'Updating...' : 'Creating...')
                  : createdCollectionId()
                    ? 'Update Collection'
                    : 'Create Collection'
              }}
            }
          </button>
        } @else if (displayedStep() === 'item') {
          @if (flowScope() === 'item-edit') {
            <button
              type="button"
              class="stac-onboarding__btn stac-onboarding__btn--secondary"
              [disabled]="!createdItemId()"
              (click)="openItemEditAssets()"
            >
              Upload Assets
            </button>
            <button
              type="button"
              class="stac-onboarding__btn stac-onboarding__btn--primary"
              [disabled]="itemSubmitting() || !canSubmitItem()"
              (click)="submitItem()"
            >
              {{
                itemSubmitting()
                  ? (createdItemId() ? 'Updating...' : 'Creating...')
                  : createdItemId()
                    ? 'Update STAC Item'
                    : 'Create STAC Item'
              }}
            </button>
          } @else {
            @if (flowScope() !== 'embedded') {
              <button type="button" class="stac-onboarding__btn stac-onboarding__btn--secondary" (click)="backToCollection()">
                Back
              </button>
            }
            <button type="button" class="stac-onboarding__btn stac-onboarding__btn--primary" [disabled]="itemSubmitting() || !canSubmitItem()" (click)="submitItem()">
              {{
                itemMode() === 'existing' && showExistingItemTab() && !isEditFlow()
                  ? 'Next'
                  : isEditFlow() && createdItemId() && itemMode() === 'existing'
                    ? itemSubmitting()
                      ? 'Updating...'
                      : hasItemChanges()
                        ? 'Update Item'
                        : 'Next'
                    : itemSubmitting()
                      ? (createdItemId() ? 'Updating...' : 'Creating...')
                      : createdItemId()
                        ? 'Update STAC Item'
                        : 'Create STAC Item'
              }}
            </button>
          }
        } @else {
          @if (flowScope() === 'item-edit') {
            <button type="button" class="stac-onboarding__btn stac-onboarding__btn--secondary" (click)="backFromItemEditAssets()">
              Back
            </button>
          } @else if (flowScope() !== 'assets-only') {
            <button type="button" class="stac-onboarding__btn stac-onboarding__btn--secondary" (click)="backToItem()">
              Back
            </button>
          }
          @if (!(showAssetsTabs() && assetsTab() === 'view')) {
            <button
              type="button"
              class="stac-onboarding__btn stac-onboarding__btn--primary"
              [disabled]="!canSubmitAssets()"
              (click)="submitAssets()"
            >
              {{ assetsSubmitting() ? 'Uploading...' : 'Submit Request' }}
            </button>
          }
        }
      </div>
    </footer>
  }
</div>

@if (bboxMapOpen()) {
  <div class="stac-map-modal" role="dialog" aria-modal="true" aria-labelledby="stac-map-title">
    <div class="stac-map-modal__backdrop" aria-hidden="true" (click)="closeBboxMap()"></div>
    <div class="stac-map-modal__panel" (click)="$event.stopPropagation()">
      <header class="stac-map-modal__header">
        <div>
          <h3 id="stac-map-title">{{ mapModalTitle() }}</h3>
          <p>{{ mapModalDescription() }}</p>
        </div>
        <button type="button" class="stac-map-modal__close" (click)="closeBboxMap()" aria-label="Close map">
          ×
        </button>
      </header>

      <div class="stac-map-modal__hint">
        <span>{{ drawInstruction() }}</span>
        <strong>{{ pendingGeometryLabel() }}</strong>
      </div>

      <div
        #bboxMap
        class="stac-map-modal__map"
        [class.stac-map-modal__map--geojson-open]="showGeoJsonPaste()"
        [style.cursor]="markerMapCursor() || null"
      >
        <input
          #geoJsonFileInput
          type="file"
          accept=".json,.geojson,application/geo+json,application/json"
          class="stac-map-modal__file-input"
          (change)="onGeoJsonFileSelected($event)"
        />

        <div
          class="stac-map-modal__map-toolbar"
          [attr.aria-label]="mapDrawMode() === 'item-geometry' ? 'Geometry import tools' : 'Geometry drawing tools'"
          (click)="$event.stopPropagation()"
        >
          @if (mapDrawMode() !== 'item-geometry') {
            <button
              type="button"
              class="stac-map-modal__tool-btn"
              [class.stac-map-modal__tool-btn--active]="activeDrawTool() === 'line'"
              title="Draw line"
              (click)="setDrawTool('line')"
            >
              <lib-canvas-toolbar-icon name="line"></lib-canvas-toolbar-icon>
            </button>

            <span class="stac-map-modal__toolbar-sep" aria-hidden="true"></span>

            <div class="stac-map-modal__shape-split">
              <button
                type="button"
                class="stac-map-modal__tool-btn stac-map-modal__shape-split__main"
                [class.stac-map-modal__tool-btn--active]="activeDrawTool() === 'circle' || activeDrawTool() === 'rectangle' || activeDrawTool() === 'polygon'"
                [title]="activeDrawTool() === 'circle' ? 'Draw circle' : activeDrawTool() === 'rectangle' ? 'Draw rectangle' : 'Draw polygon'"
                (click)="onShapeMainClick()"
              >
                @if (activeDrawTool() === 'circle') {
                  <lib-canvas-toolbar-icon name="circle"></lib-canvas-toolbar-icon>
                } @else if (activeDrawTool() === 'rectangle') {
                  <lib-canvas-toolbar-icon name="rectangle"></lib-canvas-toolbar-icon>
                } @else {
                  <lib-canvas-toolbar-icon name="polygon"></lib-canvas-toolbar-icon>
                }
              </button>
              <button
                type="button"
                class="stac-map-modal__tool-btn stac-map-modal__shape-split__chevron"
                [class.stac-map-modal__tool-btn--active]="shapeMenuOpen()"
                title="More drawing tools"
                [attr.aria-expanded]="shapeMenuOpen()"
                (click)="toggleShapeMenu($event)"
              >
                <lib-canvas-toolbar-icon name="chevron-down" imgClass="cv-toolbar-icon-img--chevron"></lib-canvas-toolbar-icon>
              </button>

              @if (shapeMenuOpen()) {
                <div class="stac-map-modal__shape-menu" role="menu">
                  @for (tool of shapeDrawTools; track tool.id) {
                    <button
                      type="button"
                      class="stac-map-modal__shape-menu-item"
                      role="menuitem"
                      (click)="selectShapeTool(tool.id, $event)"
                    >
                      <lib-canvas-toolbar-icon [name]="tool.id"></lib-canvas-toolbar-icon>
                      <span>{{ tool.label }}</span>
                    </button>
                  }
                </div>
              }
            </div>

            <span class="stac-map-modal__toolbar-sep" aria-hidden="true"></span>
          }

          <button
            type="button"
            class="stac-map-modal__tool-btn stac-map-modal__geojson-paste-trigger"
            title="Paste or import GeoJSON"
            [class.stac-map-modal__tool-btn--active]="showGeoJsonPaste()"
            (click)="toggleGeoJsonPaste()"
          >
            <svg
              class="stac-map-modal__geojson-stack-icon"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            >
              <polygon points="12 2 2 7 12 12 22 7 12 2" />
              <polyline points="2 17 12 22 22 17" />
              <polyline points="2 12 12 17 22 12" />
            </svg>
          </button>
        </div>

        <div class="stac-map-modal__zoom-controls" (click)="$event.stopPropagation()">
          <button type="button" class="stac-map-modal__zoom-btn" title="Zoom in" (click)="zoomModalMap(1)">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" aria-hidden="true">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
          </button>
          <button type="button" class="stac-map-modal__zoom-btn" title="Zoom out" (click)="zoomModalMap(-1)">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" aria-hidden="true">
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
          </button>
        </div>

        @if (showGeoJsonPaste()) {
          <div class="stac-map-modal__geojson-paste" (click)="$event.stopPropagation()">
            <div class="stac-map-modal__geojson-paste-header">
              <label for="stac-geojson-paste">Paste GeoJSON</label>
              <button
                type="button"
                class="stac-map-modal__geojson-import-link"
                (click)="triggerGeoJsonUpload()"
              >
                Import GeoJSON
              </button>
            </div>
            <textarea
              id="stac-geojson-paste"
              placeholder='{"type":"Feature","geometry":{...}}'
              [value]="geoJsonPasteText()"
              (input)="onGeoJsonPasteInput($event)"
              (paste)="onGeoJsonPaste($event)"
            ></textarea>
            <button type="button" class="stac-onboarding__btn stac-onboarding__btn--primary" (click)="applyPastedGeoJson()">
              Apply GeoJSON
            </button>
          </div>
        }

      </div>

      <footer class="stac-map-modal__footer">
        <div class="stac-map-modal__bbox">
          @if (pendingBbox()) {
            <span>BBOX</span>
            <strong>{{ pendingBboxLabel() }}</strong>
          } @else {
            No selection
          }
          @if (geoJsonHint()) {
            <em>{{ geoJsonHint() }}</em>
          }
        </div>
        <div class="stac-map-modal__actions">
          <button type="button" class="stac-onboarding__btn stac-onboarding__btn--secondary" (click)="closeBboxMap()">
            Cancel
          </button>
          <button type="button" class="stac-onboarding__btn stac-onboarding__btn--primary" [disabled]="!hasPendingMapSelection()" (click)="applyPendingBbox()">
            Save
          </button>
        </div>
      </footer>
    </div>
  </div>
}

@if (calendarPopover(); as cal) {
  <div class="stac-calendar-backdrop" (click)="closeCalendar()" aria-hidden="true"></div>
  <div
    class="stac-calendar-popover"
    [style.top.px]="cal.top"
    [style.left.px]="cal.left"
    [style.width.px]="cal.width"
    (click)="$event.stopPropagation()"
  >
    <div class="stac-calendar-popover__header">
      <div>
        <h3>{{ calendarMonthLabel(cal.month) }}</h3>
        @if (cal.field === 'endDate') {
          <p class="stac-calendar-popover__hint">End date and time must be on or after the start date and time.</p>
        }
        @if (cal.field === 'itemDate' && collectionHasDateRange()) {
          <p class="stac-calendar-popover__hint">Must be within collection range: {{ collectionDateRangeDisplay() }}</p>
        }
      </div>
      <div>
        <button type="button" (click)="previousCalendarMonth()" aria-label="Previous month">
          ‹
        </button>
        <button type="button" (click)="nextCalendarMonth()" aria-label="Next month">
          ›
        </button>
      </div>
    </div>

    <div class="stac-calendar-popover__weekdays">
      @for (day of calendarWeekDays(); track day) {
        <span>{{ day }}</span>
      }
    </div>

    <div class="stac-calendar-popover__days">
      @for (day of calendarDays(cal.month); track day.date.getTime()) {
        <button
          type="button"
          [disabled]="isCalendarDayOutsideCollectionRange(day.date, cal.field)"
          [class.stac-calendar-popover__day--muted]="!day.inCurrentMonth"
          [class.stac-calendar-popover__day--today]="isCalendarDayToday(day.date)"
          [class.stac-calendar-popover__day--in-range]="isCalendarDayInRange(day.date, cal.field)"
          [class.stac-calendar-popover__day--selected]="isCalendarDaySelected(day.date, cal.field)"
          [class.stac-calendar-popover__day--out-of-range]="isCalendarDayOutsideCollectionRange(day.date, cal.field)"
          (click)="selectCalendarDate(cal.field, day.date)"
        >
          {{ day.date.getDate() }}
        </button>
      }
    </div>
  </div>
}

@if (assetActionDropdown(); as actionMenu) {
  <div
    class="stac-asset-action-overlay"
    role="menu"
    aria-label="File actions"
    [class.stac-asset-action-overlay--above]="actionMenu.openAbove"
    [style.top.px]="actionMenu.top"
    [style.left.px]="actionMenu.left"
    (click)="$event.stopPropagation()"
  >
    <ul class="stac-asset-action-overlay__list">
      <li class="stac-asset-action-overlay__item">
        <button
          type="button"
          role="menuitem"
          class="stac-asset-action-overlay__link"
          [disabled]="downloadingAssetId() === actionMenu.asset.id"
          (click)="onAssetActionDownload()"
        >
          {{ downloadingAssetId() === actionMenu.asset.id ? 'Downloading…' : 'Download' }}
        </button>
      </li>
    </ul>
  </div>
}

@if (timePopover(); as time) {
  <div class="stac-calendar-backdrop" (click)="closeTimePopover()" aria-hidden="true"></div>
  <div
    class="stac-time-popover"
    [style.top.px]="time.top"
    [style.left.px]="time.left"
    [style.width.px]="time.width"
    (click)="$event.stopPropagation()"
  >
    <div class="stac-time-popover__header">
      <h3>Select time</h3>
      <p class="stac-time-popover__hint">UTC time for {{ time.field === 'itemDate' ? 'item' : time.field === 'startDate' ? 'start' : 'end' }}</p>
    </div>
    <div class="stac-time-popover__columns">
      <div class="stac-time-popover__column" aria-label="Hours">
        <span class="stac-time-popover__column-label">Hour</span>
        <div class="stac-time-popover__list">
          @for (hour of timeHours(); track hour) {
            <button
              type="button"
              [class.stac-time-popover__option--selected]="isTimeHourSelected(hour)"
              (click)="selectTimeHour(hour)"
            >
              {{ formatTimePart(hour) }}
            </button>
          }
        </div>
      </div>
      <div class="stac-time-popover__column" aria-label="Minutes">
        <span class="stac-time-popover__column-label">Minute</span>
        <div class="stac-time-popover__list">
          @for (minute of timeMinutes(); track minute) {
            <button
              type="button"
              [class.stac-time-popover__option--selected]="isTimeMinuteSelected(minute)"
              (click)="selectTimeMinute(minute)"
            >
              {{ formatTimePart(minute) }}
            </button>
          }
        </div>
      </div>
    </div>
  </div>
}

@if (propertiesModalOpen()) {
  <div class="stac-properties-modal" role="dialog" aria-modal="true" aria-labelledby="stac-properties-modal-title">
    <div class="stac-properties-modal__backdrop" (click)="closePropertiesModal()"></div>
    <div class="stac-properties-modal__panel" (click)="$event.stopPropagation()">
      <header class="stac-properties-modal__header">
        <h3 id="stac-properties-modal-title">Stac Item Properties</h3>
        <button
          type="button"
          class="stac-properties-modal__close"
          aria-label="Close properties"
          (click)="closePropertiesModal()"
        >
          ×
        </button>
      </header>

      <div class="stac-properties-modal__body">
        <div class="stac-properties-modal__toolbar">
          <p>Enter key-value pairs for STAC item metadata. Numbers are detected automatically.</p>
          <button
            type="button"
            class="stac-properties-modal__add"
            [disabled]="!canAddPropertyDraftRow()"
            (click)="addPropertyDraftRow()"
          >
            + Add Properties
          </button>
        </div>

        @if (propertiesModalError()) {
          <p class="stac-properties-modal__error" role="alert">{{ propertiesModalError() }}</p>
        }

        <div class="stac-properties-modal__table" role="table" aria-label="STAC item properties">
          <div class="stac-properties-modal__table-head" role="row">
            <span role="columnheader">Key</span>
            <span role="columnheader">Value</span>
            <span class="stac-properties-modal__table-head-actions" role="columnheader">Actions</span>
          </div>

          @for (row of propertiesDraft(); track row.id) {
            <div class="stac-properties-modal__table-row" role="row">
              <label class="stac-properties-modal__cell" role="cell">
                <span class="visually-hidden">Key</span>
                <input
                  type="text"
                  [value]="row.key"
                  placeholder="Example: temperature"
                  (input)="updatePropertyDraftKey(row.id, $event)"
                />
              </label>
              <label class="stac-properties-modal__cell" role="cell">
                <span class="visually-hidden">Value</span>
                <input
                  type="text"
                  [value]="row.value"
                  placeholder="Example: 32.5"
                  (input)="updatePropertyDraftValue(row.id, $event)"
                />
              </label>
              <div class="stac-properties-modal__cell stac-properties-modal__cell--actions" role="cell">
                <button
                  type="button"
                  class="stac-properties-modal__delete"
                  aria-label="Delete property"
                  (click)="removePropertyDraftRow(row.id)"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                    <polyline points="3 6 5 6 21 6"></polyline>
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                  </svg>
                </button>
              </div>
            </div>
          }
        </div>
      </div>

      <footer class="stac-properties-modal__footer">
        <button type="button" class="stac-onboarding__btn stac-onboarding__btn--primary" (click)="submitPropertiesModal()">
          Submit
        </button>
      </footer>
    </div>
  </div>
}
